import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { heroSlides } from '@/mocks/movies';
import { getMovieById } from '@/lib/queries';

export default function HeroCarousel() {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const total = heroSlides.length;

  const goTo = useCallback((next: number) => {
    setIndex(((next % total) + total) % total);
  }, [total]);

  useEffect(() => {
    if (paused) return undefined;
    const timer = window.setTimeout(() => goTo(index + 1), 6500);
    return () => window.clearTimeout(timer);
  }, [index, paused, goTo]);

  const slide = heroSlides[index];
  const movie = getMovieById(slide.movieId);

  return (
    <section
      className="relative -mt-16 h-[560px] w-full overflow-hidden bg-background-950 md:-mt-20 md:h-[680px] lg:h-[760px]"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {heroSlides.map((item, itemIndex) => (
        <div
          key={item.id}
          className={`absolute inset-0 transition-opacity duration-1000 ${
            itemIndex === index ? 'opacity-100' : 'opacity-0'
          }`}
        >
          <img
            src={item.image}
            alt={`${item.headline} 焦点影片剧照`}
            title={`${item.headline} - 日韩成人电影焦点推荐`}
            className={`h-full w-full object-cover object-top ${itemIndex === index ? 'animate-slow-zoom' : ''}`}
          />
        </div>
      ))}

      <div className="absolute inset-0 bg-gradient-to-r from-background-950 via-background-950/75 to-background-950/25" />
      <div className="absolute inset-0 bg-gradient-to-t from-background-950 via-background-950/20 to-background-950/60" />

      <div className="relative mx-auto flex h-full max-w-content flex-col justify-end px-4 pb-12 md:px-6 md:pb-16 lg:pb-20">
        <div className="max-w-2xl">
          <div className="flex flex-wrap items-center gap-3">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-accent-500 px-2.5 py-1 text-[11px] font-semibold tracking-wide text-background-950">
              <i className="ri-fire-fill text-xs" />
              {slide.kicker}
            </span>
            <span className="flex items-center gap-1.5 text-xs text-foreground-300">
              <i className="ri-star-fill text-accent-400" />
              {movie ? movie.rating.toFixed(1) : '—'} 分
            </span>
            <span className="text-xs text-foreground-400">
              {movie?.region} · {movie?.year} · {movie?.duration} 分钟
            </span>
          </div>

          <h1
            key={slide.id}
            className="animate-fade-up mt-4 text-4xl font-black leading-[1.1] tracking-tight text-foreground-50 md:text-6xl lg:text-7xl"
          >
            {slide.headline}
          </h1>
          <p
            key={`${slide.id}-caption`}
            className="animate-fade-up mt-4 max-w-xl text-sm leading-relaxed text-foreground-300 md:mt-5 md:text-base"
          >
            {slide.caption}
          </p>

          <div className="mt-7 flex flex-col gap-3 sm:flex-row sm:items-center md:mt-9">
            <Link
              to={`/detail/${slide.movieId}`}
              className="inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              查看影片资料
              <i className="ri-arrow-right-line text-base" />
            </Link>
            <Link
              to="/category/drama"
              className="inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md border border-foreground-50/25 bg-background-950/40 px-5 py-3 text-sm text-foreground-100 backdrop-blur-sm transition-colors hover:border-accent-500 hover:text-accent-400"
            >
              <i className="ri-grid-line text-base" />
              浏览全部分类
            </Link>
          </div>
        </div>

        <div className="mt-8 flex items-end justify-between gap-6 md:mt-10">
          <div className="flex items-center gap-3">
            {heroSlides.map((item, itemIndex) => (
              <button
                key={item.id}
                type="button"
                aria-label={`切换到第 ${itemIndex + 1} 张焦点影片`}
                onClick={() => goTo(itemIndex)}
                className={`h-1 cursor-pointer rounded-full transition-all duration-300 ${
                  itemIndex === index ? 'w-10 bg-accent-500' : 'w-5 bg-foreground-50/25 hover:bg-foreground-50/50'
                }`}
              />
            ))}
            <span className="ml-2 font-label text-xs tracking-[0.2em] text-foreground-400">
              <span className="text-foreground-100">{String(index + 1).padStart(2, '0')}</span>
              <span className="mx-1">/</span>
              {String(total).padStart(2, '0')}
            </span>
          </div>

          <div className="hidden items-center gap-2 md:flex">
            <button
              type="button"
              aria-label="上一张"
              onClick={() => goTo(index - 1)}
              className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-full border border-foreground-50/20 text-foreground-200 transition-colors hover:border-accent-500 hover:text-accent-400"
            >
              <i className="ri-arrow-left-s-line text-lg" />
            </button>
            <button
              type="button"
              aria-label="下一张"
              onClick={() => goTo(index + 1)}
              className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-full border border-foreground-50/20 text-foreground-200 transition-colors hover:border-accent-500 hover:text-accent-400"
            >
              <i className="ri-arrow-right-s-line text-lg" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}